"use client";

import { useEffect, useRef, useState } from "react";

import { DashedBorder } from "@/app/lib/components/ui/DashedBorder";

import classes from "./DeskScroller.module.css";

function midX(element: HTMLElement): number {
  const rect = element.getBoundingClientRect();

  return rect.left + rect.width / 2;
}

/**
 * The problem desktop's frame. Above 900px the desktop is wider than any
 * screen: it bleeds to both viewport edges, opens with the seam between its
 * two monitors under the thread that drops from the section's top, and pans
 * sideways by trackpad, scrollbar or drag. Below that the monitors stack and
 * nothing scrolls.
 */
export default function DeskScroller({ children }: { children: React.ReactNode }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const seamRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const frame = frameRef.current;
    const scroller = scrollerRef.current;
    const seam = seamRef.current;

    if (!frame || !scroller || !seam) {
      return;
    }

    // The frame spans the page's column, so its middle is the thread's line.
    // A drag in progress keeps going from the recentered desktop, not from
    // where it was before the resize.
    function centerOnSeam() {
      if (!frame || !scroller || !seam || !seam.offsetParent) {
        return;
      }

      const before = scroller.scrollLeft;

      scroller.scrollLeft += midX(seam) - midX(frame);

      if (dragRef.current) {
        dragRef.current.startScrollLeft += scroller.scrollLeft - before;
      }
    }

    centerOnSeam();
    window.addEventListener("resize", centerOnSeam);

    return () => window.removeEventListener("resize", centerOnSeam);
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    function handleMouseUp() {
      dragRef.current = null;
      setIsDragging(false);
    }

    function handleMouseMove(event: MouseEvent) {
      const drag = dragRef.current;
      const scroller = scrollerRef.current;

      // A button released outside the window sends no mouseup here, so the
      // first move back without it ends the drag instead of panning.
      if ((event.buttons & 1) === 0) {
        handleMouseUp();

        return;
      }

      if (drag && scroller) {
        scroller.scrollLeft = drag.startScrollLeft - (event.pageX - drag.startX);
      }
    }

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  function handleMouseDown(event: React.MouseEvent) {
    const scroller = scrollerRef.current;

    // Only a desktop that overflows pans; stacked, a press is just a press.
    if (event.button !== 0 || !scroller || scroller.scrollWidth <= scroller.clientWidth) {
      return;
    }

    dragRef.current = { startX: event.pageX, startScrollLeft: scroller.scrollLeft };
    setIsDragging(true);
  }

  return (
    <div className={classes.frame} ref={frameRef}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- the drag is a pointer shortcut for native sideways scrolling, which keyboards and trackpads already have */}
      <div
        className={classes.scroller}
        ref={scrollerRef}
        data-dragging={isDragging || undefined}
        onMouseDown={handleMouseDown}
      >
        <div className={classes.desk}>
          {children}
          <div className={classes.seam} ref={seamRef}>
            <DashedBorder orientation="vertical" color="var(--mantine-color-dark-3)" />
          </div>
        </div>
      </div>
      <div className={classes.hint} aria-hidden>
        ← scroll sideways →
      </div>
    </div>
  );
}
